import { IconAlert } from '@consta/icons/IconAlert'
import { IconArrowRight } from '@consta/icons/IconArrowRight'
import { Text } from '@consta/uikit/Text'
import { withTooltip } from '@consta/uikit/withTooltip'
import cn from 'classnames'
import { useCallback } from 'react'

import { typedMemo } from '../helpers'
import type { ModalErrors, ModalItem } from '../types'
import css from './ModalNavItem.css'

type ModalNavItemProps<Item extends ModalItem> = {
  item: Item
  error?: ModalErrors
  onChoose: (item: Item) => void
  isActive: boolean
}

const IconAlertWithTooltip = withTooltip({
  mode: 'mouseover',
  appearTimeout: 200,
  direction: 'rightCenter',
})(IconAlert)

export const ModalNavItem = typedMemo(function ModalNavItem<Item extends ModalItem>({
  item,
  error,
  onChoose,
  isActive,
}: ModalNavItemProps<Item>) {
  const hasError = error != null
  const Icon = hasError && !isActive ? IconAlertWithTooltip : IconArrowRight
  const tooltipProps = hasError ? { tooltipProps: { content: error.text } } : {}

  const additionalText = item.additionalText
  const isDisabled = item.disabled

  const handleOnClick = useCallback(() => {
    if (!isDisabled) onChoose(item)
  }, [isDisabled, item, onChoose])

  const className = cn(css.item, {
    [css.active]: isActive,
    [css.disabled]: isDisabled,
  })

  return (
    <span className={className} onClick={handleOnClick}>
      <span>{item.label}</span>
      <div className={css.right}>
        {!isActive && !!(hasError || additionalText) && (
          <Text
            className={css.additionalText}
            truncate={true}
            size="xs"
            view={hasError ? 'alert' : 'secondary'}>
            {hasError ? error.text : additionalText}
          </Text>
        )}
        <Icon size="xs" view={hasError ? 'alert' : 'primary'} {...tooltipProps} />
      </div>
    </span>
  )
})
